import React from 'react';
import { TextField, Box, styled } from '@mui/material';
import type { TextFieldProps } from '@mui/material';
import { alpha } from '@mui/material/styles';

// Custom textarea variants
export type TextareaVariant = 'outlined' | 'filled' | 'standard';
export type TextareaSize = 'small' | 'medium' | 'large';

// Extended props interface
export interface CustomTextareaProps
  extends Omit<TextFieldProps, 'variant' | 'size' | 'multiline'> {
  variant?: TextareaVariant;
  size?: TextareaSize;
  description?: string;
  maxLength?: number;
  showCharacterCount?: boolean;
  minRows?: number;
  maxRows?: number;
  autoResize?: boolean;
}

// Styled textarea component
const StyledTextField = styled(TextField)<CustomTextareaProps>(({ theme, size, error }) => {
  const sizeConfig = {
    small: {
      '& .MuiInputBase-root': {
        fontSize: '0.75rem',
        padding: theme.spacing(1),
      },
      '& .MuiInputLabel-root': {
        fontSize: '0.75rem',
      },
    },
    medium: {
      '& .MuiInputBase-root': {
        fontSize: '0.875rem',
        padding: theme.spacing(1.5),
      },
      '& .MuiInputLabel-root': {
        fontSize: '0.875rem',
      },
    },
    large: {
      '& .MuiInputBase-root': {
        fontSize: '1rem',
        padding: theme.spacing(2),
      },
      '& .MuiInputLabel-root': {
        fontSize: '1rem',
      },
    },
  };

  return {
    ...sizeConfig[size || 'medium'],
    '& .MuiOutlinedInput-root': {
      backgroundColor: theme.palette.grey[50],
      borderRadius: theme.spacing(1),
      transition: 'all 0.2s ease-in-out',
      '& fieldset': {
        borderColor: error ? theme.palette.error.main : theme.palette.grey[300],
        borderWidth: 1.5,
      },
      '&:hover': {
        backgroundColor: theme.palette.grey[100],
        '& fieldset': {
          borderColor: error ? theme.palette.error.main : theme.palette.primary.main,
        },
      },
      '&.Mui-focused': {
        backgroundColor: theme.palette.common.white,
        boxShadow: `0 0 0 2px ${alpha(
          error ? theme.palette.error.main : theme.palette.primary.main,
          0.2
        )}`,
        '& fieldset': {
          borderColor: error ? theme.palette.error.main : theme.palette.primary.main,
          borderWidth: 2,
        },
      },
      '&.Mui-disabled': {
        backgroundColor: theme.palette.grey[100],
        '& fieldset': {
          borderColor: theme.palette.grey[200],
        },
      },
      '& .MuiInputBase-input': {
        resize: 'vertical',
        fontFamily: theme.typography.fontFamily,
        lineHeight: 1.5,
      },
    },
    '& .MuiFilledInput-root': {
      backgroundColor: theme.palette.grey[50],
      borderRadius: theme.spacing(1, 1, 0, 0),
      '&:hover': {
        backgroundColor: theme.palette.grey[100],
      },
      '&.Mui-focused': {
        backgroundColor: theme.palette.common.white,
      },
      '& .MuiInputBase-input': {
        resize: 'vertical',
      },
    },
    '& .MuiInput-root': {
      '&:before': {
        borderBottomColor: theme.palette.grey[300],
      },
      '&:hover:not(.Mui-disabled):before': {
        borderBottomColor: theme.palette.primary.main,
      },
      '&.Mui-focused:after': {
        borderBottomColor: error ? theme.palette.error.main : theme.palette.primary.main,
      },
      '& .MuiInputBase-input': {
        resize: 'vertical',
      },
    },
    '& .MuiFormHelperText-root': {
      marginLeft: 0,
      marginTop: theme.spacing(0.5),
      fontSize: '0.75rem',
      '&.Mui-error': {
        color: theme.palette.error.main,
      },
    },
  };
});

const CharacterCount = styled('span')<{ isOverLimit?: boolean }>(({ theme, isOverLimit }) => ({
  fontSize: '0.75rem',
  color: isOverLimit ? theme.palette.error.main : theme.palette.text.secondary,
  float: 'right',
  marginTop: theme.spacing(0.5),
  fontWeight: isOverLimit ? 500 : 400,
}));

export const Textarea: React.FC<CustomTextareaProps> = ({
  variant = 'outlined',
  size = 'medium',
  description,
  maxLength,
  showCharacterCount,
  minRows = 3,
  maxRows = 6,
  autoResize = true,
  value = '',
  error,
  helperText,
  label,
  ...props
}) => {
  const characterCount = typeof value === 'string' ? value.length : 0;
  const isOverLimit = maxLength ? characterCount > maxLength : false;

  const displayError = error || isOverLimit;
  const displayHelperText = displayError
    ? isOverLimit
      ? `Character limit exceeded (${characterCount}/${maxLength})`
      : helperText
    : description || helperText;

  return (
    <Box sx={{ width: '100%' }}>
      <StyledTextField
        {...props}
        variant={variant}
        size={size as 'small' | 'medium' | undefined}
        multiline
        rows={autoResize ? undefined : minRows}
        minRows={autoResize ? minRows : undefined}
        maxRows={autoResize ? maxRows : undefined}
        value={value}
        error={displayError}
        label={label}
        helperText={displayHelperText}
        inputProps={{
          maxLength,
          style: {
            resize: autoResize ? 'none' : 'vertical',
          },
          ...props.inputProps,
        }}
        InputProps={{
          sx: {
            alignItems: 'flex-start',
          },
          ...props.InputProps,
        }}
      />
      {showCharacterCount && maxLength && (
        <CharacterCount isOverLimit={isOverLimit}>
          {characterCount}/{maxLength}
        </CharacterCount>
      )}
    </Box>
  );
};

export default Textarea;
